import { Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { PdfCompanyLogo, PdfCompanyTitle, pdfStyles } from '../../..';
import { Company } from '../../../../interfaces/company.interface';
import { BILL_EASE_VAT_NUMBER } from '../../../../interfaces/enumerables';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { ProductInOrder } from '../../../../interfaces/product.interface';
import { PdfProductsSection } from '../products/PdfProductsSection';
import { PdfTotalCost } from '../products/PdfTotalCost';
import { PdfTotalQuantities } from '../products/PdfTotalQuantities';
import { PdfAadeData } from './PdfAadeData';
import { PdfBankDetails } from './PdfBankDetails';
import { PdfCopyright } from './PdfCopyright';
import { PdfCustomerData } from './PdfCustomerData';
import { PdfCustomerDebt } from './PdfCustomerDebt';
import { PdfDocTitle } from './PdfDocTitle';
import { PdfExtraInfo } from './PdfExtraInfo';
import { PdfNotes } from './PdfNotes';
import { PdfQrCode } from './PdfQrCode';

interface Props {
  company: Company;
  doc: InvoiceInterface;
  products: ProductInOrder[];
  index: number;
  numberOfPages: number;
  logo: string | null | undefined;
}

export const PdfPage = ({ company, doc, products, index, numberOfPages, logo }: Props) => {
  const getBankDetails = useCallback(() => {
    if (company?.vatNumber === BILL_EASE_VAT_NUMBER && doc.notes?.includes('RF ')) {
      return null;
    }

    return <PdfBankDetails bankDetails={company?.bankDetails || []} />;
  }, [company?.vatNumber, company?.bankDetails, doc?.notes]);

  // Mirrors `PdfQrCode.tsx`'s own gate - needed here too, since the notes box's width depends on
  // whether a QR column is actually going to share the row with it.
  const showQr = !!doc?.sentToAade;

  const getBotSection = useCallback(() => {
    if (index + 1 === numberOfPages) {
      return (
        <View>
          <View style={styles.containerCost}>
            <View>
              <View style={{ marginBottom: 6 }}>
                <PdfTotalQuantities doc={doc} />
              </View>
              <PdfCustomerDebt doc={doc} />
            </View>
            <View>{getBankDetails()}</View>
            <PdfTotalCost doc={doc} />
          </View>
          <View style={{ marginTop: 2, flexDirection: 'row', alignItems: 'flex-start' }}>
            {/* With no QR sibling in the row, `flexGrow: 1` correctly fills the full width - the
            bug below only happens with a sibling actually competing for space. With a QR sibling,
            `flexGrow: 1` measures/wraps `PdfNotes`' <Text> at the row's full, pre-flexGrow width,
            then draws the box at the correct (narrower) computed width - the box border ends up
            right, but longer notes text overflows straight through the QR box to its right.
            Confirmed by rendering both ways with real long notes text. A4 width (595.28) minus
            `section`'s margin+padding (20+20) minus the QR column (4pt padding + 56pt QR) =
            495.28. */}
            <View style={showQr ? { borderWidth: 1, width: 495.28 } : { borderWidth: 1, flexGrow: 1 }}>
              <PdfNotes doc={doc} />
              <PdfCopyright doc={doc} />
            </View>
            {showQr && (
              <View style={{ paddingLeft: 4 }}>
                <PdfQrCode doc={doc} />
              </View>
            )}
          </View>
        </View>
      );
    }
  }, [doc, numberOfPages, index, getBankDetails, showQr]);

  return (
    <Page size="A4" style={styles.page}>
      {/*{getLogo()}*/}
      <View style={styles.index}>
        <Text style={pdfStyles.smallTitleRegular}>
          {index + 1}/{numberOfPages}
        </Text>
      </View>
      <View style={styles.section}>
        <View>
          <View style={pdfStyles.flexStartSpaceRow}>
            <PdfCompanyTitle company={company} />
            <PdfCompanyLogo logo={logo} />
          </View>
          <View style={{ paddingTop: 10 }}>
            <PdfDocTitle doc={doc} />
          </View>
          <View style={[{ paddingTop: 4 }, pdfStyles.flexStartStartRow]}>
            <PdfCustomerData doc={doc} />
            <PdfExtraInfo doc={doc} />
          </View>
          {/* TODO: Enable when feature is ready */}
          {/*<PdfRelatedDocs doc={doc} />*/}
          <View style={{ paddingTop: 4 }}>
            <PdfProductsSection
              doc={doc}
              products={products}
              docHasVatExceptionCategory={!!doc.vatExceptionCategoryId}
            />
          </View>
        </View>
        {getBotSection()}
      </View>
      <View style={styles.footer}>
        <PdfAadeData doc={doc} />
      </View>
    </Page>
  );
};

const styles = StyleSheet.create({
  index: {
    position: 'absolute',
    top: 2,
    left: 6,
  },
  page: {
    fontFamily: 'Manrope',
    fontSize: 12,
    flexDirection: 'column',
    backgroundColor: 'white',
  },
  section: {
    margin: 10,
    padding: 10,
    marginBottom: 60,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  containerCost: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  footer: {
    position: 'absolute',
    bottom: 2,
    left: 10,
    width: '100%',
  },
  logoContainer: {
    position: 'absolute',
    top: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  logo: {
    width: 60,
    height: 30,
  },
});
